import React, { useState } from "react";
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Divider,
  Toolbar,
  Typography,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import DashboardIcon from "@mui/icons-material/Dashboard";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useNavigate, useLocation } from "react-router-dom";
import navGroups from "./navGroups";

const DRAWER_WIDTH = 230;
const COLLAPSED_WIDTH = 64;

export default function AdminSidebar({ sidebarOpen, setSidebarOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState(null);

  const toggleGroup = (title) => {
    setOpenGroup((prev) => (prev === title ? null : title));
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <>
      {/* COLLAPSE BUTTON */}
      <div
        onClick={() => setSidebarOpen(!sidebarOpen)}
        style={{
          position: "fixed",
          top: "50%",
          left: sidebarOpen ? DRAWER_WIDTH - 12 : COLLAPSED_WIDTH - 12,
          transform: "translateY(-50%)",
          zIndex: 1400,
          transition: "left 0.3s ease",
        }}
      >
        <div
          style={{
            width: 28,
            height: 48,
            borderRadius: 24,
            background: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(8px)",
            border: "1px solid rgba(255,255,255,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          }}
        >
          {sidebarOpen ? (
            <ChevronLeftIcon className="w-4 h-4 text-slate-700" />
          ) : (
            <ChevronRightIcon className="w-4 h-4 text-slate-700" />
          )}
        </div>
      </div>

      <Drawer
        variant="permanent"
        sx={{
          width: sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: sidebarOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
            transition: "width 0.3s ease",
            boxSizing: "border-box",
            borderRight: "none",
            overflow: "hidden",
            background:
              "linear-gradient(180deg, #0F4C81 0%, #1976D2 45%, #42A5F5 100%)",
            color: "#fff",
          },
        }}
      >
        {/* HEADER */}
        <Toolbar
          sx={{
            minHeight: 56,
            px: sidebarOpen ? 2 : 1,
            justifyContent: sidebarOpen ? "flex-start" : "center",
          }}
        >
          {sidebarOpen && (
            <Typography fontSize={14} fontWeight={700}>
              Admin Portal
            </Typography>
          )}
        </Toolbar>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.15)" }} />

        {/* MENU */}
        <List sx={{ px: 1, pt: 0.5 }}>
          {/* DASHBOARD */}
          <ListItemButton
            onClick={() => handleNavigate("/admin")}
            sx={{
              py: 1,
              mb: 0.6,
              borderRadius: 1,
              bgcolor:
                location.pathname === "/admin"
                  ? "rgba(255,255,255,0.22)"
                  : "transparent",
              "&:hover": {
                bgcolor: "rgba(255,255,255,0.18)",
              },
              justifyContent: sidebarOpen ? "flex-start" : "center",
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: "#fff" }}>
              <DashboardIcon sx={{ fontSize: 20 }} />
            </ListItemIcon>

            {sidebarOpen && (
              <ListItemText
                primary="Dashboard"
                primaryTypographyProps={{
                  fontSize: 14,
                  fontWeight: 600,
                }}
              />
            )}
          </ListItemButton>

          <Divider sx={{ my: 0.8, borderColor: "rgba(255,255,255,0.15)" }} />

          {navGroups.map((group) => {
            const hasPages = Array.isArray(group.pages);
            const isOpen = openGroup === group.title;
            const GroupIcon = group.groupIcon;

            return (
              <React.Fragment key={group.title}>
                <ListItemButton
                  onClick={() =>
                    hasPages
                      ? toggleGroup(group.title)
                      : handleNavigate(group.path)
                  }
                  sx={{
                    py: 0.9,
                    mb: 0.4,
                    borderRadius: 1,
                    justifyContent: sidebarOpen ? "flex-start" : "center",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.18)",
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: "#fff" }}>
                    {GroupIcon && <GroupIcon sx={{ fontSize: 20 }} />}
                  </ListItemIcon>

                  {sidebarOpen && (
                    <ListItemText
                      primary={group.title}
                      primaryTypographyProps={{
                        fontSize: 13.5,
                        fontWeight: 600,
                      }}
                    />
                  )}

                  {sidebarOpen &&
                    hasPages &&
                    (isOpen ? (
                      <ExpandLess sx={{ fontSize: 18 }} />
                    ) : (
                      <ExpandMore sx={{ fontSize: 18 }} />
                    ))}
                </ListItemButton>

                {hasPages && (
                  <Collapse in={isOpen && sidebarOpen}>
                    <List disablePadding sx={{ pl: 3 }}>
                      {group.pages.map((page) => {
                        const active =
                          location.pathname === page.path;

                        return (
                          <ListItemButton
                            key={page.id}
                            onClick={() => handleNavigate(page.path)}
                            sx={{
                              py: 0.7,
                              mb: 0.3,
                              borderRadius: 1,
                              bgcolor: active
                                ? "rgba(255,255,255,0.28)"
                                : "transparent",
                              "&:hover": {
                                bgcolor: "rgba(255,255,255,0.22)",
                              },
                            }}
                          >
                            <ListItemText
                              primary={page.name}
                              primaryTypographyProps={{
                                fontSize: 13,
                                fontWeight: active ? 600 : 500,
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                )}
              </React.Fragment>
            );
          })}
        </List>
      </Drawer>
    </>
  );
}
